import React, { useState, useEffect, useRef } from 'react';
import { Pencil } from 'lucide-react';

type EditableTextProps = {
  value: string;
  fallbackValue?: string;
  onChange: (newValue: string) => void;
  disabled?: boolean;
  maxLength?: number;
  className?: string;
  inputClassName?: string;
  displayClassName?: string;
  placeholder?: string;
  showEditIcon?: boolean;
};

const EditableText: React.FC<EditableTextProps> = ({
  value,
  fallbackValue = "",
  onChange,
  disabled = false,
  maxLength = 50,
  className = "py-3 px-4 font-medium text-sm sm:text-base md:text-lg",
  inputClassName = "w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:border-BAMPink focus:border-2",
  displayClassName = "",
  placeholder = "Click to edit",
  showEditIcon = true,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || fallbackValue);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  useEffect(() => {
    setEditValue(value || fallbackValue);
  }, [value, fallbackValue]);

  const handleClick = () => {
    if (!disabled) {
      setIsEditing(true);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditValue(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveChanges();
    } else if (e.key === 'Escape') {
      setIsEditing(false);
      setEditValue(value || fallbackValue);
    }
  };

  const handleBlur = () => {
    saveChanges();
  };

  const saveChanges = () => {
    if (editValue.trim() !== '') {
      onChange(editValue.trim());
    } else {
      setEditValue(value || fallbackValue);
    }
    setIsEditing(false);
  };

  const displayValue = value || fallbackValue || placeholder;
  
  return (
    <div className={className}>
      {isEditing ? (
        <input
          ref={inputRef}
          type="text"
          className={inputClassName}
          value={editValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          maxLength={maxLength}
          placeholder={placeholder}
        />
      ) : (
        <div 
          onClick={handleClick}
          className={`${disabled ? '' : 'cursor-pointer rounded px-2 py-1 group'} 
                     ${displayClassName} flex items-center gap-2`}
        >
          {displayValue}
          {showEditIcon && !disabled && (
            <Pencil className="h-4 w-4 invisible group-hover:visible" />
          )}
        </div>
      )}
    </div>
  );
};

export default EditableText;
